// External Dependencies
import { connect } from 'react-redux';
import { groupBy, orderBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

import { getHasRecipeVariants } from '@selectors/featureProps';
import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { getClientInfo } from '@selectors/client';

import recipeService from '@services/recipe';

import { buildExportRecipeRows } from '../../utils/formatRecipesForExport';
import utilsAnalytics from '../../utils/index';

const BATCH_RECIPES_TO_FETCH_AT_ONCE = 50;

const sortComponentRows = (rows) =>
  orderBy(rows, ['name', 'associatedProductName', 'componentQuantity'], ['asc', 'asc', 'desc']);

export const ExportRecipeModal = (props) => {
  const {
    params: {
      recipesIds: propsRecipesIds,
      title: propsTitle,
      customParams,
      selectedStatus,
      selectedBrands,
    },
    currency,
    hasVariants,
    client: { hasMultipleBrands, defaultChannelId, clientChannels },
    closeModal,
  } = props;

  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recipesIds, setRecipesIds] = useState(propsRecipesIds);
  const [detailedRecipesList, setDetailedRecipesList] = useState([]);
  const [detailedRecipesWithComponents, setDetailedRecipesWithComponents] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [allRecipesIds, setAllRecipesIds] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial');

  // Promise of the variant compositions, started as soon as the exported recipe ids are known so
  // its server-side computation overlaps with the main recipe detail fetching. Null when the client
  // has no variant feature.
  const variantDataPromiseRef = useRef(null);
  const rawRecipeByIdRef = useRef(new Map());

  const isInCentralMode = getIsInCentralKitchenOnlyPage();

  const channelsForExport = isInCentralMode
    ? clientChannels.filter(({ id }) => id === defaultChannelId)
    : clientChannels;

  const buildRecipeRows = (recipes) =>
    buildExportRecipeRows(recipes, hasMultipleBrands, channelsForExport, defaultChannelId);

  const startVariantFetch = (recipeIds) => {
    if (!hasVariants) {
      return;
    }

    variantDataPromiseRef.current = recipeService.getVariantCompositions(recipeIds);
  };

  const exitModal = () => {
    setTitle('');
    setProgress(0);
    setLoading(false);
    setRecipesIds([]);
    setDetailedRecipesList([]);
    setDetailedRecipesWithComponents([]);

    closeModal();
  };

  const exportReady = () => {
    setTitle(i18next.t('GENERAL.EXPORT_SUCCESS'));
    setLoading(false);
  };

  const exportFailure = () => {
    setTitle(i18next.t('GENERAL.EXPORT_FAILURE'));
    setLoading(false);
  };

  // Phase 1: export the selected recipes directly, or start fetching all recipe ids by filters.
  useEffect(() => {
    if (propsTitle) {
      setTitle(propsTitle);
    }

    if (propsRecipesIds && propsRecipesIds.length) {
      startVariantFetch(propsRecipesIds);

      setAllRecipesIds(propsRecipesIds);
      setRecipesIds(propsRecipesIds);
      setFetchingPhase('processing_details');

      return;
    }

    if (customParams && selectedStatus) {
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Phase 2: fetch paginated recipe ids
  const fetchNextBatch = async () => {
    try {
      const { data, count } = await recipeService.getPaginated(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_RECIPES_TO_FETCH_AT_ONCE,
          search: '',
        },
        {
          ...(hasMultipleBrands && {
            brandIds: {
              inArray: (selectedBrands || []).map(({ id }) => id),
            },
          }),
          isKitchen: { eq: isInCentralMode },
          active: { eq: selectedStatus?.filterValue ?? false },
          quantity: customParams.quantity,
          categoryId: customParams.categoryId,
          parentsCount: customParams.parentsCount,
          mappingsCount: customParams.mappingsCount,
          unit: customParams.unit,
        },
      );

      const batchIds = (data || []).map(({ id }) => id);
      const updatedAllIds = [...allRecipesIds, ...batchIds];

      setAllRecipesIds(updatedAllIds);

      const hasMorePages =
        data && data.length === BATCH_RECIPES_TO_FETCH_AT_ONCE && updatedAllIds.length < count;

      if (hasMorePages) {
        setCurrentPage(currentPage + 1);

        return;
      }

      // All recipe ids known: start the variant fetch so it overlaps with the details
      startVariantFetch(updatedAllIds);

      setRecipesIds([...updatedAllIds]);
      setFetchingPhase('processing_details');
    } catch {
      exportFailure();
    }
  };

  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Phase 3: fetch recipe details in batches
  const loadRecipesByBatch = async (remainingIds) => {
    if (!remainingIds.length) {
      setFetchingPhase('finalizing');

      return;
    }

    const batchIds = remainingIds.slice(0, BATCH_RECIPES_TO_FETCH_AT_ONCE);

    try {
      const recipes = await recipeService.getAssociatedRecipesByIds(batchIds, true, true);

      recipes.forEach((recipe) => {
        rawRecipeByIdRef.current.set(recipe.recipe.id, recipe);
      });

      const { recipeRows, componentRows } = buildRecipeRows(recipes);

      if (!loading) {
        return;
      }

      const nextIds = remainingIds.filter((id) => !batchIds.includes(id));

      setDetailedRecipesWithComponents((prev) => [...prev, ...componentRows]);
      setDetailedRecipesList((prev) => [...prev, ...recipeRows]);
      setRecipesIds(nextIds);
    } catch {
      exportFailure();
    }
  };

  useEffect(() => {
    if (fetchingPhase !== 'processing_details') {
      return;
    }

    setProgress(allRecipesIds.length ? 100 - (recipesIds.length / allRecipesIds.length) * 100 : 0);

    loadRecipesByBatch([...recipesIds]);
  }, [recipesIds, fetchingPhase]);

  /**
   * Build the variant rows grouped by recipe id. Each variant reuses the main recipe metadata and
   * only swaps in its own composition. Variants with no own composition are skipped.
   */
  const buildVariantRowsByRecipeId = (compositions) => {
    const recipeRowsByRecipeId = {};
    const componentRowsByRecipeId = {};

    compositions.forEach(({ recipeId, name, compositionSettings, allergens }) => {
      const rawRecipe = rawRecipeByIdRef.current.get(recipeId);

      if (!rawRecipe || !compositionSettings.length) {
        return;
      }

      const variantRecipe = { ...rawRecipe, compositionSettings, allergens };
      const { recipeRows, componentRows } = buildRecipeRows([variantRecipe]);

      const stampedRecipeRows = recipeRows.map((row) => ({ ...row, variantName: name }));
      const stampedComponentRows = componentRows.map((row) => ({ ...row, variantName: name }));

      recipeRowsByRecipeId[recipeId] = [
        ...(recipeRowsByRecipeId[recipeId] || []),
        ...stampedRecipeRows,
      ];
      componentRowsByRecipeId[recipeId] = [
        ...(componentRowsByRecipeId[recipeId] || []),
        ...stampedComponentRows,
      ];
    });

    return { recipeRowsByRecipeId, componentRowsByRecipeId };
  };

  /**
   * Expand the export by variant, grouped by recipe: each recipe yields its main row (labelled
   * "main recipe") followed by one row per variant. Component rows follow the same grouping.
   */
  const expandByVariant = (mainRecipeRows, mainComponentRows, variantRows) => {
    const { recipeRowsByRecipeId, componentRowsByRecipeId } = variantRows;
    const mainVariantName = i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB');

    const expandedRecipes = mainRecipeRows.reduce((acc, mainRow) => {
      acc.push({ ...mainRow, variantName: mainVariantName });
      (recipeRowsByRecipeId[mainRow.id] || []).forEach((row) => acc.push(row));

      return acc;
    }, []);

    const mainComponentsByRecipeId = groupBy(mainComponentRows, 'recipeId');

    const expandedComponents = mainRecipeRows.reduce((acc, mainRow) => {
      (mainComponentsByRecipeId[mainRow.id] || []).forEach((row) => {
        acc.push({ ...row, variantName: mainVariantName });
      });
      (componentRowsByRecipeId[mainRow.id] || []).forEach((row) => acc.push(row));

      return acc;
    }, []);

    return { expandedRecipes, expandedComponents };
  };

  // Phase 4: await the overlapped variant fetch, then generate the XLS
  useEffect(() => {
    if (fetchingPhase !== 'finalizing') {
      return;
    }

    const finalize = async () => {
      try {
        const sortedComponents = sortComponentRows(detailedRecipesWithComponents);

        const variantData = variantDataPromiseRef.current
          ? await variantDataPromiseRef.current
          : { compositions: [] };

        if (!loading) {
          return;
        }

        const hasVariantColumn = variantData.compositions.length > 0;

        if (!hasVariantColumn) {
          utilsAnalytics.exportRecipes(
            detailedRecipesList,
            sortedComponents,
            currency,
            channelsForExport,
            hasMultipleBrands,
            isInCentralMode,
            false,
          );

          return exportReady();
        }

        const variantRows = buildVariantRowsByRecipeId(variantData.compositions);
        const { expandedRecipes, expandedComponents } = expandByVariant(
          detailedRecipesList,
          sortedComponents,
          variantRows,
        );

        utilsAnalytics.exportRecipes(
          expandedRecipes,
          expandedComponents,
          currency,
          channelsForExport,
          hasMultipleBrands,
          isInCentralMode,
          true,
        );

        return exportReady();
      } catch {
        return exportFailure();
      }
    };

    finalize();
  }, [fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      exitModal={exitModal}
      isLoading={loading}
      progress={progress}
      titleModal={title}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
});

export default connect(mapStateToProps)(ExportRecipeModal);
